(() => {
  'use strict';
  const instances = new WeakMap();
  const validHex = value => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
  const noop = () => {};

  function mount(form, template) {
    instances.get(form)?.dispose();
    try {
      const gallery = form?.closest?.('.q-product-layout')?.querySelector?.('[data-product-gallery]');
      const media = gallery?.querySelector?.('.q-product-media');
      const source = template?.content?.querySelector?.('figure[data-raster-preview]');
      if (!media || !source) return { update: noop, dispose: noop };
      const figure = source.cloneNode(true);
      const colors = [...figure.querySelectorAll('[data-raster-color]')];
      const masks = [...figure.querySelectorAll('[data-raster-mask]')];
      const images = [...masks, figure.querySelector('[data-raster-shading]'), figure.querySelector('[data-raster-highlights]')];
      const urls = images.map(image => image?.getAttribute('data-src')?.trim() || '');
      if (!colors.length || !masks.length || urls.some(url => !url || !/^(https?:)?\/\//i.test(url) && !url.startsWith('/'))) {
        return { update: noop, dispose: noop };
      }

      figure.hidden = true;
      media.appendChild(figure);
      let disposed = false;
      let started = false;
      let failed = false;
      let desired = null;
      const ready = () => images.every(image => image.complete && image.naturalWidth > 0);
      const hide = () => {
        figure.hidden = true;
        gallery.classList.remove('q-product-gallery--raster-preview');
      };
      const render = () => {
        if (disposed || !desired || failed || !ready()) { hide(); return; }
        desired.parts.forEach((part) => {
          part.color.style.setProperty('--q-preview-color', part.hex.toUpperCase());
        });
        if (desired.parts.length === 1) figure.style.setProperty('--q-preview-color', desired.parts[0].hex.toUpperCase());
        const label = desired.parts.map(part => {
          const colorLabel = typeof part.label === 'string' ? part.label.trim() : '';
          const partLabel = desired.parts.length > 1 && typeof part.partLabel === 'string' ? part.partLabel.trim() : '';
          return partLabel && colorLabel ? `${partLabel}: ${colorLabel}` : colorLabel;
        }).filter(Boolean).join(', ');
        if (label) figure.setAttribute('aria-label', label);
        else figure.removeAttribute('aria-label');
        figure.hidden = false;
        gallery.classList.add('q-product-gallery--raster-preview');
      };
      const onLoad = () => render();
      const onError = () => { failed = true; hide(); };
      images.forEach(image => {
        image.addEventListener('load', onLoad);
        image.addEventListener('error', onError);
      });

      const instance = {
        update({ active = false, hex = '', label = '', parts } = {}) {
          if (disposed) return;
          const requested = Array.isArray(parts) && parts.length ? parts : [{ id: '', hex, label }];
          const resolved = requested.map((part) => {
            const color = colors.find(node => node.getAttribute('data-part-id') === part.id) ||
              (requested.length === 1 && colors.length === 1 ? colors[0] : null);
            const mask = masks.find(node => node.getAttribute('data-part-id') === part.id) ||
              (requested.length === 1 && masks.length === 1 ? masks[0] : null);
            return { ...part, color, mask };
          });
          desired = active === true && requested.length === colors.length && requested.length === masks.length &&
            resolved.every(part => part.color && part.mask && validHex(part.hex)) &&
            new Set(resolved.map(part => part.color)).size === resolved.length &&
            new Set(resolved.map(part => part.mask)).size === resolved.length ? { parts: resolved } : null;
          if (!desired) { hide(); return; }
          if (!started) {
            started = true;
            resolved.forEach(part => {
              const maskUrl = (part.mask.getAttribute('data-src') || '').replace(/["\\\n\r\f]/g, '');
              part.color.style.setProperty('--q-preview-mask', `url("${maskUrl}")`);
            });
            images.forEach((image, index) => image.setAttribute('src', urls[index]));
          }
          render();
        },
        dispose() {
          if (disposed) return;
          disposed = true;
          images.forEach(image => {
            image.removeEventListener('load', onLoad);
            image.removeEventListener('error', onError);
          });
          hide();
          figure.remove();
          if (instances.get(form) === instance) instances.delete(form);
        },
      };
      instances.set(form, instance);
      return instance;
    } catch {
      return { update: noop, dispose: noop };
    }
  }

  window.QProductRasterPreview = { mount };
})();
